import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { FeatureTooltipsProvider } from '@hotelplan/libs.context.feature-tooltips';
import { formatDate, parseDateStr } from '@hotelplan/libs.utils';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import {
  isFdrWishlistProduct,
  isFdrWishlistProductHotel,
} from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist.types';
import { FdrOfferItem } from 'fdr/components/local/fdr-offer';
import {
  getFdrProductBreadcrumbs,
  getFdrProductTa,
} from 'fdr/components/local/fdr-page-components/recommendations/product/fdr-product-recommendation.utils';
import { getFdrProductIcons } from 'fdr/components/local/fdr-travel-types/fdr-travel-type.utils';
import { WISHLIST_OFFER_DATE_FORMAT } from 'fdr/components/local/fdr-wishlist/fdr-wishlist.constants';
import { getOfferLinkFromWishlistItem } from 'fdr/components/local/fdr-wishlist/fdr-wishlist.utils';
import { useFdrGetActiveWishlistQuery } from 'fdr/schemas/query/wishlist/fdr-get-active-wishlist.generated';

interface ISRLMapResultsProps {
  items: { id: string }[];
  onClose?(): void;
}

const MapCardsList = styled.ul(
  sx2CssThemeFn({
    mt: ['20px', '30px'],
    '.offer-close-btn': {
      left: 'auto',
      right: '8px',
      '.icon': {
        height: '20px',
        width: '20px',
      },
    },
  })
);

const FdrWishlistMapResults: React.FC<ISRLMapResultsProps> = ({
  items,
  onClose,
}) => {
  const { mobile } = useDeviceType();
  const [t] = useTranslation(['common']);

  const { data } = useFdrGetActiveWishlistQuery({
    skip: !items.length,
    ssr: false,
  });

  const productIds = items.map(item => item.id);
  const wishlistItems =
    data?.fdrActiveWishlist.wishlist?.wishlistItems?.filter(item => {
      return productIds.includes(item.product.id);
    }) || [];

  return (
    <FeatureTooltipsProvider preventContainerOverlapping={mobile}>
      <MapCardsList data-id="map-cards-list">
        {wishlistItems.map(wishlistItem => {
          if (!isFdrWishlistProduct(wishlistItem?.product)) {
            return null;
          }

          const link = getOfferLinkFromWishlistItem(wishlistItem);
          const product = wishlistItem.product;
          const offerToShow = wishlistItem.trackedOffer?.latest
            ? wishlistItem.trackedOffer.latest
            : wishlistItem.trackedOffer.original;

          return (
            <li key={`product-${product.id}`}>
              <FdrOfferItem
                productType={product.__typename}
                image={product?.hero}
                rating={
                  isFdrWishlistProductHotel(product)
                    ? product.rating
                    : undefined
                }
                title={product.name}
                breadcrumbs={getFdrProductBreadcrumbs(product)}
                ta={getFdrProductTa(product)}
                productFeatures={product.summaryFeatureGroups}
                link={link}
                price={offerToShow.pricePerPerson}
                productTravelType={getFdrProductIcons(
                  offerToShow.travelType,
                  product.__typename
                )}
                departureDate={formatDate(
                  parseDateStr(offerToShow.departureDate),
                  WISHLIST_OFFER_DATE_FORMAT
                )}
                returnDate={formatDate(
                  parseDateStr(offerToShow.returnDate),
                  WISHLIST_OFFER_DATE_FORMAT
                )}
                duration={t('common:nights.count', {
                  count: offerToShow.duration,
                })}
                mealType={offerToShow.rooms
                  .map(r => r.mealDescription)
                  .filter(Boolean)
                  .join(',')}
                onClose={onClose}
                transferPrice={offerToShow.transferPrice}
              />
            </li>
          );
        })}
      </MapCardsList>
    </FeatureTooltipsProvider>
  );
};

export default FdrWishlistMapResults;
